import { styled } from '@mui/material/styles'
import { Box, Paper, Button, Typography, Collapse } from '@mui/material'

// Shared with the rest of the analytics-style pages so the bulk download
// screens visually slot into the same family. Mirrors the token palette used
// in pages/pace_analytics/styled.js.
export const palette = {
  primary: '#1565c0',
  primaryLight: '#e3f2fd',
  primaryBorder: '#90caf9',
  success: '#2e7d32',
  successLight: '#e8f5e9',
  warning: '#ed6c02',
  warningLight: '#fff4e5',
  error: '#c62828',
  errorLight: '#ffebee',
  grey50: '#fafafa',
  grey100: '#f5f5f5',
  grey200: '#eeeeee',
  grey300: '#e0e0e0',
  grey600: '#757575',
  grey800: '#424242',
  white: '#ffffff',
}

// ─── Page Layout ────────────────────────────────────────────────────────────
export const PageWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}))

// ─── Section Card (filter / upload / result) ────────────────────────────────
export const SectionCard = styled(Paper)({
  borderRadius: 16,
  border: `1px solid ${palette.grey200}`,
  boxShadow: 'none',
  overflow: 'hidden',
})

export const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  padding: theme.spacing(1.5, 2.5),
  borderBottom: `1px solid ${palette.grey200}`,
  backgroundColor: palette.grey50,
}))

export const SectionIconBox = styled(Box)(({ tone = 'primary' }) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  ...(tone === 'primary' && {
    backgroundColor: palette.primaryLight,
    color: palette.primary,
  }),
  ...(tone === 'success' && {
    backgroundColor: palette.successLight,
    color: palette.success,
  }),
  ...(tone === 'warning' && {
    backgroundColor: palette.warningLight,
    color: palette.warning,
  }),
}))

export const SectionTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.85rem',
  color: palette.grey800,
})

export const SectionSubtitle = styled(Typography)({
  fontSize: '0.7rem',
  color: palette.grey600,
  marginLeft: 'auto',
})

export const SectionBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  '& form': {
    width: '100%',
  },
}))

// ─── Time-slot toggle row ───────────────────────────────────────────────────
export const ToggleRow = styled(Box)(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1.25),
  borderRadius: 8,
  backgroundColor: active ? palette.primaryLight : palette.grey50,
  border: `1px solid ${active ? palette.primaryBorder : palette.grey200}`,
  transition: 'background-color 120ms ease, border-color 120ms ease',
}))

export const ToggleHelper = styled(Typography)({
  fontSize: '0.7rem',
  color: palette.grey600,
  marginLeft: 'auto',
})

export const SlotPanel = styled(Collapse)(({ theme }) => ({
  '& .MuiCollapse-wrapperInner': {
    paddingTop: theme.spacing(1.5),
  },
}))

// ─── Buttons ────────────────────────────────────────────────────────────────
export const RunBtn = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.8rem',
  padding: theme.spacing(0.85, 2.5),
  boxShadow: 'none',
  whiteSpace: 'nowrap',
  '&:hover': { boxShadow: 'none' },
}))

export const ResetBtn = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.8rem',
  padding: theme.spacing(0.85, 2.5),
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
}))

// ─── Guided-step layout (Option 4) ─────────────────────────────────────────
export const StepRow = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  borderBottom: `1px solid ${palette.grey200}`,
  '&:last-of-type': { borderBottom: 'none' },
}))

export const StepHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.75),
}))

export const StepNumber = styled(Box)(({ muted }) => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.72rem',
  fontWeight: 700,
  flexShrink: 0,
  ...(muted
    ? { backgroundColor: palette.grey100, color: palette.grey600 }
    : { backgroundColor: palette.primary, color: palette.white }),
}))

export const StepTitle = styled(Typography)({
  fontSize: '0.82rem',
  fontWeight: 600,
  color: palette.grey800,
  lineHeight: 1.2,
})

export const StepSubtitle = styled(Typography)({
  fontSize: '0.68rem',
  color: palette.grey600,
  lineHeight: 1.3,
})

export const StepContent = styled(Box)({
  paddingLeft: 28 + 12,
})

export const StepActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.25),
  padding: theme.spacing(2, 2.5),
  backgroundColor: palette.grey50,
  borderTop: `1px solid ${palette.grey200}`,
}))

// ─── Result chips (shown after a successful upload returns S3 links) ────────
export const ResultBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1.5),
}))

export const DownloadChip = styled(Button)(({ theme, tone = 'primary' }) => ({
  borderRadius: 999,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.75rem',
  padding: theme.spacing(0.5, 1.5),
  boxShadow: 'none',
  border: '1px solid',
  ...(tone === 'primary' && {
    color: palette.primary,
    borderColor: palette.primaryBorder,
    backgroundColor: palette.primaryLight,
    '&:hover': { backgroundColor: palette.primaryLight, boxShadow: 'none' },
  }),
  ...(tone === 'success' && {
    color: palette.success,
    borderColor: '#a5d6a7',
    backgroundColor: palette.successLight,
    '&:hover': { backgroundColor: palette.successLight, boxShadow: 'none' },
  }),
  ...(tone === 'warning' && {
    color: palette.warning,
    borderColor: '#ffcc80',
    backgroundColor: palette.warningLight,
    '&:hover': { backgroundColor: palette.warningLight, boxShadow: 'none' },
  }),
  '&:hover': { boxShadow: 'none' },
}))

export const InfoBanner = styled(Box)(({ theme, tone = 'warning' }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 1.5),
  borderRadius: 8,
  fontSize: '0.75rem',
  marginTop: theme.spacing(1.5),
  ...(tone === 'info' && {
    backgroundColor: palette.primaryLight,
    border: `1px solid ${palette.primaryBorder}`,
    color: palette.primary,
  }),
  ...(tone === 'warning' && {
    backgroundColor: palette.warningLight,
    border: `1px solid #ffcc80`,
    color: '#a04000',
  }),
}))
